import {BankAccountResponseBody} from '@dms/api/shared';

export interface BankAccount extends BankAccountResponseBody {}

/**
 * @internal
 * Low-level utility for finding default bank account from array.
 * Components should use `getDefaultBankAccountFromBranch` instead.
 *
 * Uses the following priority:
 * 1. Returns the primary account (isPrimary === true)
 * 2. If no primary account, returns the first account in the array if selectFirstIfNoPrimary is true (defaults to false)
 * 3. If no accounts available, returns null
 *
 * @param bankAccounts - Array of bank accounts to search, may be undefined or null
 * @param selectFirstIfNoPrimary - If true, selects the first account if no primary account is found. Defaults to false.
 * @returns The default bank account or null if none available
 *
 * @example
 * ```typescript
 * const accounts = [
 *   { accountNumber: '123', isPrimary: false },
 *   { accountNumber: '456', isPrimary: true }
 * ];
 * const defaultAccount = getDefaultBankAccount(accounts);
 * // Returns { accountNumber: '456', isPrimary: true }
 * ```
 *
 * @example
 * ```typescript
 * const accounts = [
 *   { accountNumber: '123', isPrimary: false }
 * ];
 * const defaultAccount = getDefaultBankAccount(accounts, true);
 * // Returns { accountNumber: '123', isPrimary: false } (first account when selectFirstIfNoPrimary is true)
 * ```
 */
export const getDefaultBankAccount = (
  bankAccounts: BankAccount[] | undefined | null,
  selectFirstIfNoPrimary = false
): BankAccount | null => {
  if (!bankAccounts || bankAccounts.length === 0) {
    return null;
  }

  const primaryBankAccount = bankAccounts.find((account) => account.isPrimary);

  if (primaryBankAccount) {
    return primaryBankAccount;
  }

  if (selectFirstIfNoPrimary) {
    return bankAccounts[0];
  }

  return null;
};
